import React from 'react';

/**
 * Kolega design-system — Badge
 * Tone-based pill for severity, finding status, PR state, CVSS, counts.
 * Colors derive from the design tokens (theme-aware via :root / .dark).
 */
export type BadgeTone =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'open'
  | 'review'
  | 'resolved'
  | 'muted'
  | 'pr'
  | 'accent'
  | 'neutral';

const TONES: Record<BadgeTone, { fg: string; bg: string; bd: string }> = {
  critical: { fg: 'var(--sev-critical)', bg: 'var(--sev-critical-bg)', bd: 'var(--sev-critical-border)' },
  high: { fg: 'var(--sev-high)', bg: 'var(--sev-high-bg)', bd: 'var(--sev-high-border)' },
  medium: { fg: 'var(--sev-medium)', bg: 'var(--sev-medium-bg)', bd: 'var(--sev-medium-border)' },
  low: { fg: 'var(--sev-low)', bg: 'var(--sev-low-bg)', bd: 'var(--sev-low-border)' },
  open: { fg: 'var(--status-open)', bg: 'var(--status-open-bg)', bd: 'rgba(248,113,113,0.3)' },
  review: { fg: 'var(--status-review)', bg: 'var(--status-review-bg)', bd: 'rgba(201,209,79,0.3)' },
  resolved: { fg: 'var(--status-resolved)', bg: 'var(--status-resolved-bg)', bd: 'rgba(74,222,128,0.3)' },
  muted: { fg: 'var(--status-muted)', bg: 'var(--status-muted-bg)', bd: 'rgba(154,160,166,0.25)' },
  pr: { fg: 'var(--purple)', bg: 'var(--purple-soft)', bd: 'var(--purple-border)' },
  accent: { fg: 'var(--accent-ink)', bg: 'var(--lime-soft)', bd: 'var(--lime-soft-border)' },
  neutral: { fg: 'var(--text-muted)', bg: 'var(--surface-3)', bd: 'var(--border-subtle)' },
};

export interface BadgeProps {
  children: React.ReactNode;
  tone?: BadgeTone;
  outline?: boolean;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

export function Badge({ children, tone = 'neutral', outline = false, icon, size = 'sm', style }: BadgeProps) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: size === 'md' ? '3px 9px' : '2px 8px',
        fontSize: size === 'md' ? 12 : 11,
        fontWeight: 700,
        lineHeight: 1.3,
        borderRadius: 'var(--radius-xs)',
        color: t.fg,
        background: outline ? 'transparent' : t.bg,
        border: `1px solid ${t.bd}`,
        fontFamily: 'var(--font-sans)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon}
      {children}
    </span>
  );
}

export default Badge;
